import { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus, Send, Square, Mic, Paperclip, Trash2, Copy, Download, Printer, RefreshCw,
  Sparkles, Settings2, PanelLeft, Check, Loader2, MessageSquare, FileUp, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/components/ai/Markdown";
import { AiConfigPanel } from "@/components/ai/AiConfigPanel";
import { AiConfirmCard } from "@/components/ai/AiConfirmCard";
import { LogoMark } from "@/components/Brand";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "Which medicines are low on stock right now?",
  "Show me today's sales summary",
  "List medicines expiring in the next 60 days",
  "How many patients are currently admitted?",
];

const AssistantActions = ({ text, onRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => { const ok = await copyText(text); if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); } else toast.error("Copy not permitted by the browser."); };
  const download = () => {
    const blob = new Blob([text], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `ajclassic-ai-${Date.now()}.md`; a.click();
    URL.revokeObjectURL(a.href);
  };
  const print = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    w.document.write(`<html><head><title>AJ Classic HMS AI</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#0f172a;line-height:1.6;white-space:pre-wrap}</style></head><body>${text.replace(/</g, "&lt;")}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  const Btn = ({ onClick, icon: Icon, label, tid }) => (
    <button onClick={onClick} data-testid={tid} title={label}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-slate-500 hover:bg-slate-100 hover:text-slate-800">
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  return (
    <div className="mt-2 flex items-center gap-0.5" data-testid="assistant-actions">
      <Btn onClick={copy} icon={copied ? Check : Copy} label="Copy response" tid="copy-response-btn" />
      <Btn onClick={download} icon={Download} label="Download" tid="download-response-btn" />
      <Btn onClick={print} icon={Printer} label="Print" tid="print-response-btn" />
      {onRegenerate && <Btn onClick={onRegenerate} icon={RefreshCw} label="Regenerate" tid="regenerate-btn" />}
    </div>
  );
};

export default function AiCopilot() {
  const { user } = useAuth();
  const isAdmin = ["platform_owner", "business_admin", "hospital_admin", "pharmacy_admin"].includes(user?.role);

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const fileRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/conversations");
      setConversations(data.conversations);
      return data.conversations;
    } catch { return []; }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, toolStatus]);

  const openConversation = async (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/ai/conversations/${id}`);
      setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
    } catch (e) { toast.error(apiError(e)); }
  };

  const newConversation = async () => {
    try {
      const { data } = await api.post("/ai/conversations", { title: "New conversation" });
      setConversations((c) => [data.conversation, ...c]);
      setActiveId(data.conversation.conversation_id);
      setMessages([]);
      setSidebarOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/conversations/${id}`);
      setConversations((c) => c.filter((x) => x.conversation_id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); }
    } catch (err) { toast.error(apiError(err)); }
  };

  const clearConversation = () => { if (activeId) deleteConversationAndReset(activeId); };
  const deleteConversationAndReset = async (id) => {
    try { await api.delete(`/ai/conversations/${id}`); } catch { /* ignore */ }
    setConversations((c) => c.filter((x) => x.conversation_id !== id));
    setActiveId(null); setMessages([]);
  };

  const stop = () => { abortRef.current?.abort(); setStreaming(false); setToolStatus(""); };

  const runChat = async (text, convId) => {
    setStreaming(true);
    setToolStatus("");
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/ai/conversations/${convId}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }), signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "The AI service could not respond.");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          let ev; try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.type === "delta") {
            setToolStatus("");
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, role: "assistant", content: (last.content || "") + ev.text };
              return copy;
            });
          } else if (ev.type === "tool") {
            const verb = ev.name?.startsWith("propose_") ? "Preparing" : "Looking up";
            setToolStatus(`${verb} ${ev.name.replace(/^propose_/, "").replace(/_/g, " ")}…`);
          } else if (ev.type === "pending_action") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, role: "assistant", pending: ev.action };
              return copy;
            });
          } else if (ev.type === "error") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, role: "assistant", content: `⚠️ ${ev.message}` };
              return copy;
            });
          }
        }
      }
      loadConversations();
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && !last.content) copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${err.message}` };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      setToolStatus("");
      abortRef.current = null;
    }
  };

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;
    let convId = activeId;
    if (!convId) {
      try {
        const { data } = await api.post("/ai/conversations", { title: text.slice(0, 60) });
        convId = data.conversation.conversation_id;
        setActiveId(convId);
        setConversations((c) => [data.conversation, ...c]);
      } catch (e) { toast.error(apiError(e)); return; }
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    await runChat(text, convId);
  };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (lastUser && !streaming) {
      setMessages((m) => (m[m.length - 1]?.role === "assistant" ? m.slice(0, -1) : m));
      runChat(lastUser.content, activeId);
    }
  };

  const resolveAction = async (idx, action, decision) => {
    try {
      const { data } = await api.post(`/ai/pending-actions/${action.action_id}`, { decision });
      setMessages((m) => {
        const c = [...m];
        if (c[idx]) c[idx] = { ...c[idx], pendingDone: true, pendingResult: data };
        return [...c, { role: "assistant", content: (data.ok ? "✅ " : "") + data.message }];
      });
      if (decision === "confirm" && data.ok) toast.success(data.message);
      else if (decision === "cancel") toast.info("Cancelled — nothing was saved.");
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- voice ----
  const toggleRecord = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) setInput((v) => (v ? `${v} ${data.text}` : data.text));
          else toast.info("Didn't catch that — please try again.");
        } catch (e) { toast.error(apiError(e)); }
        finally { setTranscribing(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error("Microphone permission denied or unavailable."); }
  };

  // ---- upload ----
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const kind = file.type === "application/pdf" ? "pdf" : "image";
      const { data } = await api.post(`/ai/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded`);
      setMessages((m) => [...m, { role: "user", content: `📎 Uploaded **${file.name}**` },
        { role: "assistant", content: data.note }]);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const empty = messages.length === 0;

  return (
    <div className="fixed inset-0 top-16 flex lg:left-[268px]" data-testid="ai-copilot-page">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "block" : "hidden"} absolute inset-y-0 left-0 z-30 w-72 border-r border-slate-200 bg-white lg:static lg:block`}>
        <div className="flex h-full flex-col p-3">
          <button onClick={newConversation} data-testid="new-conversation-btn"
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 hover:border-slate-400">
            <Plus className="h-4 w-4" /> New conversation
          </button>
          <div className="mt-4 flex-1 space-y-1 overflow-y-auto" data-testid="conversation-list">
            <p className="overline px-2 pb-1 text-slate-400">History</p>
            {conversations.length === 0 && <p className="px-2 text-xs text-slate-400">No conversations yet.</p>}
            {conversations.map((c) => (
              <div key={c.conversation_id} onClick={() => openConversation(c.conversation_id)}
                data-testid={`conversation-item-${c.conversation_id}`}
                className={`group flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[13px] ${activeId === c.conversation_id ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="flex-1 truncate">{c.title}</span>
                <button onClick={(e) => deleteConversation(c.conversation_id, e)} data-testid="delete-conversation-btn"
                  className="opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" /></button>
              </div>
            ))}
          </div>
        </div>
      </aside>
      {sidebarOpen && <button className="absolute inset-0 z-20 bg-slate-900/30 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close" />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col bg-white">
        {/* header */}
        <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
          <div className="flex items-center gap-2.5">
            <button onClick={() => setSidebarOpen((o) => !o)} data-testid="toggle-ai-sidebar" className="rounded-md p-1.5 hover:bg-slate-100 lg:hidden">
              <PanelLeft className="h-4 w-4 text-slate-600" />
            </button>
            <LogoMark className="h-7 w-7" />
            <div>
              <p className="flex items-center gap-1.5 font-ui text-[13px] font-bold tracking-tight text-slate-900">
                AJ Classic HMS AI <Sparkles className="h-3.5 w-3.5 text-[#EA580C]" />
              </p>
              <p className="text-[11px] text-slate-400">Hospital &amp; Pharmacy AI Copilot</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {!empty && (
              <button onClick={clearConversation} data-testid="clear-conversation-btn"
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] text-slate-500 hover:bg-slate-100 hover:text-slate-800">
                <Trash2 className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setShowConfig(true)} data-testid="open-ai-config-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-600 hover:border-slate-400">
                <Settings2 className="h-3.5 w-3.5" /> Configure
              </button>
            )}
          </div>
        </div>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="messages-scroll">
          <div className="mx-auto w-full max-w-3xl px-4 py-6">
            {empty ? (
              <div className="mt-6 text-center" data-testid="ai-empty-state">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#0F172A] text-white">
                  <Sparkles className="h-6 w-6" />
                </span>
                <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-900">How can I help you today?</h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                  Ask about patients, stock, sales, suppliers, appointments or staff. I only read data you're
                  permitted to see — and never change records.
                </p>
                <div className="mx-auto mt-7 grid max-w-xl gap-2 sm:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="suggestion-btn"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-[13px] text-slate-600 hover:border-slate-400 hover:bg-slate-50">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((m, i) => (
                  <div key={i} data-testid={`message-${m.role}`}>
                    {m.role === "user" ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#0F172A] px-4 py-2.5 text-[15px] leading-relaxed text-white">
                          <Markdown>{m.content}</Markdown>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#0F172A] text-white">
                          <Sparkles className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          {m.content ? <Markdown>{m.content}</Markdown>
                            : (streaming && i === messages.length - 1 && (
                              <p className="flex items-center gap-2 text-sm text-slate-400">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {toolStatus || "Thinking…"}
                              </p>
                            ))}
                          {m.content && !(streaming && i === messages.length - 1) && (
                            <AssistantActions text={m.content} onRegenerate={i === messages.length - 1 ? regenerate : null} />
                          )}
                          {m.pending && (
                            <AiConfirmCard action={m.pending} done={m.pendingDone} result={m.pendingResult}
                              disabled={streaming && i === messages.length - 1}
                              onResolve={(d) => resolveAction(i, m.pending, d)} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {streaming && toolStatus && messages[messages.length - 1]?.content && (
                  <p className="flex items-center gap-2 pl-10 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> {toolStatus}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* composer */}
        <div className="border-t border-slate-200 bg-white px-4 py-3">
          <div className="mx-auto w-full max-w-3xl">
            <div className="flex items-end gap-2 rounded-2xl border border-slate-300 bg-white px-3 py-2 focus-within:border-slate-900">
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUpload} data-testid="file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" title="Upload PDF or image"
                className="mb-1 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
              </button>
              <textarea rows={1} value={input} data-testid="ai-input"
                onChange={(e) => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={recording ? "Listening… tap the mic to stop" : transcribing ? "Transcribing…" : "Message AJ Classic HMS AI…"}
                className="max-h-40 flex-1 resize-none bg-transparent py-1.5 text-[15px] outline-none placeholder:text-slate-400" />
              <button onClick={toggleRecord} disabled={transcribing} data-testid="mic-btn" title="Voice input"
                className={`mb-1 rounded-lg p-1.5 ${recording ? "animate-pulse bg-red-50 text-red-500" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"} disabled:opacity-50`}>
                {transcribing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mic className="h-5 w-5" />}
              </button>
              {streaming ? (
                <button onClick={stop} data-testid="stop-btn" title="Stop generating"
                  className="mb-0.5 grid h-9 w-9 place-items-center rounded-xl bg-[#0F172A] text-white hover:bg-[#1E293B]">
                  <Square className="h-4 w-4" fill="currentColor" />
                </button>
              ) : (
                <button onClick={() => send()} disabled={!input.trim()} data-testid="send-btn" title="Send"
                  className="mb-0.5 grid h-9 w-9 place-items-center rounded-xl bg-[#0F172A] text-white hover:bg-[#1E293B] disabled:opacity-40">
                  <Send className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-400">
              AJ Classic HMS AI reads live data scoped to your access. It can make mistakes — verify important details.
            </p>
          </div>
        </div>
      </div>

      {showConfig && <AiConfigPanel onClose={() => setShowConfig(false)} />}
    </div>
  );
}
